import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { HttpHeaders, HttpParameterCodec, HttpParams } from '@angular/common/http';
import { Params } from '@angular/router';
import { environment } from '@env/environment';
import {
  API_DEFAULT_LIMIT,
  API_DEFAULT_OFFSET,
  ApiAccessLevel,
  ApiEndpoint,
  ApiFilterValues,
  ApiGetListRequest,
  ApiGetListResponse,
  ApiGetRequest,
  ApiRequest,
  ApiRequestHeader,
  ApiRequestValues,
  ApiSearchValues,
} from '../models/api.models';
import { UserRole } from '../models/user.models';
import { assertArray, isEmptyArray } from './array.utilities';
import { filterObject, isNullOrUndefined } from './object.utilities';
import { camelToSnake, snakeToCamelCase } from './string.utilities';

export class CustomURLEncoder implements HttpParameterCodec {
  encodeKey(key: string): string {
    return key;
  }
  encodeValue(key: string): string {
    return key;
  }
  decodeKey(key: string): string {
    return key;
  }
  decodeValue(key: string) {
    return key;
  }
}
export function getRequestHttpParams(params: Params): HttpParams {
  params = filterObject(params, (value) => !isNullOrUndefined(value)) || {};
  // && value !== '' we need to allows empty values for filter params

  return new HttpParams({
    encoder: new CustomURLEncoder(),
    fromObject: Object.keys(params).reduce((acc, key) => {
      if (key.startsWith('search_') && key !== 'search_fields') acc[key] = encodeURIComponent(params[key]);
      else acc[key] = params[key];

      return acc;
    }, {}),
  });
}

export function parseGetListRequestParams<T extends object>(request: ApiGetListRequest<T>): Params {
  const paging: Params = request.disablePaging
    ? {}
    : {
        limit: (request.limit && Math.max(1, request.limit)) || API_DEFAULT_LIMIT,
        offset: (request.offset && Math.max(0, request.offset)) || API_DEFAULT_OFFSET,
      };

  return {
    ...paging,
    ...parseGetRequestParams(request),
  };
}

export function parseGetRequestParams<T extends object>(request: ApiGetRequest<T>): Params {
  request.filterValues = {
    ...request.filterValues,
    ...request.fixedFilterValues,
  };

  const mutationValues: Params = ['sort', 'filter', 'search'].reduce(
    (params, type) => ({
      ...params,
      ...Object.entries(request[`${type}Values`] || ({} as ApiRequestValues<T>)).reduce(
        (values, [key, value]) => ({
          ...values,
          ...(value == null
            ? {}
            : {
                [`${type}_${camelToSnake(key)}`]: assertArray(value)
                  .map((entry) => entry.toString()) //encodeURIComponent(entry.toString()) //since we use HTTPParams() http.js encodes params
                  .join(','),
              }),
        }),
        {},
      ),
    }),
    request.params || {},
  );

  const searchParams: Params =
    request.searchTerm == null
      ? {}
      : {
          search_term: request.searchTerm, //encodeURIComponent(request.searchTerm), //since we use HTTPParams() http.js encodes params
          search_fields: assertArray(request.searchFields)
            .filter((field) => field != null)
            .map((field) => camelToSnake(field))
            .join(','),
        };

  const includeExcludeCols = ['include', 'exclude'].reduce((params, type) => {
    const cols = assertArray(request[type])
      .filter((col) => col != null)
      .map((col) => camelToSnake(col));

    return {
      ...params,
      ...(isEmptyArray(cols) ? {} : { [type]: cols.join(',') }),
    };
  }, {});

  const groupBy = request.groupBy ? { group_by: request.groupBy } : {};

  return {
    ...searchParams,
    ...mutationValues,
    ...includeExcludeCols,
    ...groupBy,
  };
}

export function getUrlFromRequest(request: ApiRequest): string {
  const routes = [request.endpoint, ...assertArray(request.route)].filter((path) => path != null && path !== '');
  const source = request.endpoint === ApiEndpoint.Signup ? '' : environment.app.resources.dashboard;

  return `${source}${routes.join('/')}`;
}

export function userRoleToAccessLevel(role?: UserRole): ApiAccessLevel {
  const parsedRole = role?.replace('_', '-').toLowerCase() as ApiAccessLevel;

  return Object.values(ApiAccessLevel).includes(parsedRole) ? parsedRole : ApiAccessLevel.None;
}

export function accessLevelToHeaders(accessLevel?: ApiAccessLevel, headers?: HttpHeaders): HttpHeaders {
  headers = headers || new HttpHeaders();

  if (accessLevel && accessLevel !== ApiAccessLevel.None) {
    headers = headers.set(ApiRequestHeader.AccessLevel, accessLevel);
  }

  return headers;
}

export function impersonationToHeaders(impersonationId?: number, headers?: HttpHeaders): HttpHeaders {
  headers = headers || new HttpHeaders();

  if (impersonationId) {
    headers = headers.set(ApiRequestHeader.Impersonate, impersonationId.toString());
  }

  return headers;
}

export function getFirstFromlistResponse<T extends object>() {
  return (source: Observable<ApiGetListResponse<T>>): Observable<T> => source.pipe(map(({ data }) => data?.[0]));
}

export function mergeApiRequests<T extends ApiRequest>(
  source: Partial<T>,
  target?: Partial<T>,
  mergeFilterValues?: boolean,
): T {
  const merged = Object.entries(target || {}).reduce(
    (newReq, [key, value]) => ({
      ...newReq,
      ...(value != null ? { [key]: value } : {}),
    }),
    source || {},
  ) as T;

  if (mergeFilterValues && merged.hasOwnProperty('filterValues')) {
    (merged as any).filterValues = {
      ...((source as any)?.filterValues || {}),
      ...((target as any)?.filterValues || {}),
    };
  }

  if (mergeFilterValues && merged.hasOwnProperty('fixedFilterValues')) {
    (merged as any).fixedFilterValues = {
      ...((source as any)?.fixedFilterValues || {}),
      ...((target as any)?.fixedFilterValues || {}),
    };
  }

  return merged;
}

export function filtersToApiValues(filters: { key: string; value: string }[], prefix: 'filter'): ApiFilterValues;
export function filtersToApiValues(filters: { key: string; value: string }[], prefix: 'search'): ApiSearchValues;
export function filtersToApiValues(
  filters: { key: string; value: string }[],
  prefix: 'filter' | 'search',
): ApiSearchValues | ApiFilterValues {
  return filters.reduce((apiFilters, filter) => {
    if (filter.key.startsWith(prefix)) {
      apiFilters[snakeToCamelCase(filter.key.replace(`${prefix}_`, ''))] = filter.value;
    }
    return apiFilters;
  }, {});
}
